// src/components/lessons/renderers/StepByStepRenderer.jsx
import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';

const StepByStepRenderer = ({ block, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);

  const processText = (text) => {
    if (typeof text !== 'string') return text;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) => {
      return index % 2 === 1 ? (
        <strong key={index} style={{
          fontWeight: '700',
          color: '#000000',
          background: '#FFFFFF',
          padding: '0.125rem 0.375rem',
          borderRadius: '0.25rem',
          border: '1px solid #E5E7EB'
        }}>
          {part}
        </strong>
      ) : part;
    });
  };

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '2px solid #000000',
        borderRadius: '0.75rem',
        padding: isMobile ? '1.25rem' : '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: isHovered ? '0 8px 24px rgba(0, 0, 0, 0.12)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Blue accent bar at top */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: '#3A86FF'
      }} />

      {/* Title */}
      {block.title && (
        <h4 style={{
          fontSize: isMobile ? '1rem' : '1.1rem',
          fontWeight: '700',
          color: '#000000',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: '#000000',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'transform 0.3s ease',
            transform: isHovered ? 'rotate(12deg) scale(1.1)' : 'rotate(0deg) scale(1)'
          }}>
            <CheckCircle size={16} style={{ color: '#3A86FF' }} />
          </div>
          {block.title}
        </h4>
      )}

      {/* Instruction */}
      {block.instruction && (
        <div style={{
          background: '#FAFBFC',
          padding: isMobile ? '1rem' : '1.25rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          // borderLeft: '4px solid #3A86FF', // <-- REMOVED THIS LINE
          border: '1px solid #E5E7EB',
          borderLeft: '4px solid #3A86FF'
        }}>
          <p style={{
            fontSize: isMobile ? '0.9rem' : '0.95rem',
            color: '#000000',
            margin: 0,
            lineHeight: '1.7',
            opacity: 0.9
          }}>
            {processText(block.instruction)}
          </p>
        </div>
      )}
    {/* Description (for exam_section blocks) */}
      {block.description && (
        <div style={{
          background: '#FAFBFC',
          padding: isMobile ? '1rem' : '1.25rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          border: '1px solid #E5E7EB',
          borderLeft: '4px solid #3A86FF'
        }}>
          <p style={{
            fontSize: isMobile ? '0.9rem' : '0.95rem',
            color: '#000000',
            margin: 0,
            lineHeight: '1.7',
            opacity: 0.9
          }}>
            {processText(block.description)}
          </p>
        </div>
      )}
        {/* Sub-parts (for exam_section blocks) */}
      {block.sub_parts && Array.isArray(block.sub_parts) && (
        <div style={{ marginBottom: block.example ? '1rem' : 0 }}>
          {block.sub_parts.map((subPart, index) => (
            <div
              key={index}
              style={{
                background: '#FFFFFF',
                padding: isMobile ? '1rem' : '1.25rem',
                borderRadius: '0.5rem',
                marginBottom: '0.75rem',
                border: '2px solid #E5E7EB',
                borderLeft: '4px solid #3A86FF',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Sub-part title */}
              <p style={{
                fontSize: isMobile ? '0.9rem' : '0.95rem',
                fontWeight: '700',
                color: '#000000',
                margin: 0,
                marginBottom: '0.5rem',
                lineHeight: '1.4',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#3A86FF'
                }} />
                {processText(subPart.title)}
              </p>

              {/* Sub-part task */}
              <p style={{
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                color: '#000000',
                margin: 0,
                lineHeight: '1.6',
                paddingLeft: '1rem',
                opacity: 0.75
              }}>
                {processText(subPart.task)}
              </p>
            </div>
          ))}
        </div>
      )}
      {/* Redemittel */}
      {block.redemittel && (
        <div style={{
          background: '#000000',
          padding: isMobile ? '1rem' : '1.25rem',
          borderRadius: '0.5rem',
          marginBottom: block.example ? '1rem' : 0
        }}>
          <p style={{
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            color: '#FFFFFF',
            margin: 0,
            marginBottom: '0.75rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#3A86FF'
            }} />
            Redemittel:
          </p>
          <p style={{
            fontSize: isMobile ? '0.9rem' : '0.95rem',
            color: '#FFFFFF',
            margin: 0,
            lineHeight: '1.6',
            opacity: 0.95
          }}>
            {processText(block.redemittel)}
          </p>
        </div>
      )}

      {/* Example */}
      {block.example && (
        <div style={{
          background: '#FAFBFC',
          padding: isMobile ? '1rem' : '1.25rem',
          borderRadius: '0.5rem',
          border: '1px solid #E5E7EB',
          borderLeft: '4px solid #000000'
        }}>
          <p style={{
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            color: '#000000',
            margin: 0,
            marginBottom: '0.75rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#3A86FF'
            }} />
            Example:
          </p>

          {typeof block.example === 'string' ? (
            <p style={{
              fontSize: isMobile ? '0.9rem' : '0.95rem',
              color: '#000000',
              margin: 0,
              lineHeight: '1.6',
              opacity: 0.85
            }}>
              {processText(block.example)}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {Object.entries(block.example).map(([key, value]) => (
                <div key={key} style={{
                  display: 'flex',
                  gap: '0.75rem',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  padding: '0.5rem',
                  background: '#FFFFFF',
                  borderRadius: '0.375rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <span style={{
                    fontWeight: '700',
                    color: '#000000',
                    minWidth: '80px'
                  }}>
                    {key}:
                  </span>
                  <span style={{ color: '#000000', opacity: 0.8 }}>
  {processText(value)}
</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Decorative corner dot */}
      {isHovered && (
        <div style={{
          position: 'absolute',
          bottom: '12px',
          right: '12px',
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: '#3A86FF',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      )}
    </div>
  );
};

// Exporting the component name defined in *this* file
export default StepByStepRenderer;